// THE PUBLIC STOREFRONT ENTRYPOINT — AND THE RULE IT EXISTS TO MAKE ENFORCEABLE.
//
// NOTHING REACHABLE FROM THIS FILE MAY IMPORT THE VIEW LIBRARY. QUAL-03 requires
// the storefront course page to ship **zero bytes of React**, and Phase 8 is the
// phase that builds that page. This entrypoint exists NOW, before there is any
// storefront at all, because its existence is the whole of what makes the
// manifest half of QUAL-03 a real, non-vacuous gate from the first commit:
// app/lib/gates/react_bytes.rb walks the build manifest transitively from every
// entrypoint declared in config/storefront_entrypoints.yml, over BOTH its
// `imports` and its `dynamicImports` edges, and yields a finding for any
// reachable chunk the build reports as containing a module from the view
// library's package.
//
// The regression this guards is a single-pull-request one: a shared component
// that quietly imports React, or a lazily `import()`ed island pulled into a
// storefront partial, moves the storefront budget in one commit. Latency
// regresses gradually and is caught by measurement; this does not.
//
// So keep this bundle Hotwire-only — Turbo and Stimulus, server-rendered HTML,
// which is also what gives the Arabic RTL storefront correct SEO.

// ===========================================================================
// THE STYLESHEET, ADDED 2026-09-21 BY PLAN 06a-25 UNDER A FOUNDER RULING BY
// majdy. It closes broken window 392, and it is a one-line repair of a hole
// that was measured rather than suspected.
//
// ⚠ UNTIL THIS LINE EXISTED THIS ENTRYPOINT CARRIED NO CSS AT ALL, in all
// three Vite manifests including production. The lesson partial selects this
// entrypoint for EVERY lesson render and the layout emits CSS only for the
// chosen one, so every lesson page this repository can serve shipped ZERO
// stylesheet links — and four of phase 6A’s own UI-SPEC obligations, U-06,
// U-08, U-14 and U-24, had no surface on which they could be measured. The
// lesson bidi system spec records the numbers, the mechanism and the geometry.
//
// ⚠ THIS DOES NOT WEAKEN THE RULE THIS FILE EXISTS TO MAKE ENFORCEABLE. A CSS
// import pulls in no module from the view library, so the zero-bytes gate
// walks the same transitive closure it always did and finds the same nothing.
// That was PROVEN rather than assumed: the gate was re-run against a fresh
// production build with this line in place, and the result is recorded in plan
// 06a-25’s SUMMARY. It did surface one real defect in the build’s chunk map,
// which was fixed at the cause in vite.config.ts rather than tolerated.
//
// The stylesheet lives one directory up rather than beside this file, for the
// gate reason the sibling entrypoint records above its identical import.
// ===========================================================================
import "../styles/application.css"

// THE PLAYER LOADS ON CLICK (D-21, O-4; plan 07A-17): a storefront page ships a sized poster and a
// play button, and nothing of the player, so the page stays cacheable because the token travels
// only in the `no-store` answer. The listener is shared with the student entrypoint (plan 11a-07);
// its file says why it imports the player only on the first press.
import { listenForPlayerLaunch } from "../player/launch_on_click"

listenForPlayerLaunch()

// TURBO FRAMES AND STIMULUS, FOR THE LIVE SEARCH (STORE-03, D-11; plan
// 08A-16). The core `@hotwired/turbo`, not `@hotwired/turbo-rails`, which
// would bring the Action Cable client. Turbo Drive is switched off: storefront
// navigation stays plain document loads, so a page is what its shared body
// says and nothing is prefetched or snapshotted; only a frame given a `src`
// loads through Turbo. The controllers are storefront-only and registered
// here, never through controllers/index.js, which belongs to the instructor
// bundle.
import { session } from "@hotwired/turbo"
import { Application } from "@hotwired/stimulus"
import SearchController from "../storefront/search_controller"
import ConsentController from "../storefront/consent_controller"
import PixelLoaderController from "../storefront/pixel_loader_controller"
import { keepPrivateFrameDefaults } from "../storefront/private_frames"
import ImpersonationCountdownController from "../controllers/impersonation_countdown_controller"

session.drive = false
keepPrivateFrameDefaults()

const application = Application.start()
application.register("storefront-search", SearchController)

// THE CONSENT BANNER AND THE PIXEL LOADER (STORE-08; plan 08A-27). Vendor scripts load only through
// the loader, after consent where the academy asks for it, from origins the page's CSP names.
application.register("storefront-consent", ConsentController)
application.register("storefront-pixel-loader", PixelLoaderController)
// The operator's impersonation bar (plan 21-15, A-15) renders on this layout too.
application.register("impersonation-countdown", ImpersonationCountdownController)

// A DEVELOPMENT BANNER ONLY (06a-REVIEW IN-03). Vite replaces the flag with a
// literal false in a production build and drops the statement, so no shipped
// bundle writes to a visitor console.
if (import.meta.env.DEV) console.log("Lumna: storefront bundle")
